import React from 'react';
import { autobind } from 'core-decorators';
import { Row, Col } from 'src/componentes/grid';
import Dialog from 'src/componentes/dialog';
import Button from 'src/componentes/button';
import { FormattedMessage, injectIntl } from 'react-intl';
import LucideIcon from 'src/componentes/lucide-icon';

import SmartVariableCalculationSelector from '../shared/SmartVariableCalculationSelector';
import ItemLookup from 'src/lookups/item';
import FormulaPreview from '../shared/FormulaPreview';
import InputNumber from 'src/componentes/input-number';

@autobind
class BasicFunctionModal extends React.Component {
  static defaultProps = {
    isOpen: false,
    tipo: 'soma', // 'soma' | 'produto' | 'media' | 'mediaPonderada'
    titulo: 'Soma',
    operador: '+',
    // Props de contexto para o SmartVariableCalculationSelector
    isItem: true,
    tipoItem: null,
    tipoEntidade: null,
    isCestaIndicadores: false,
    modulosPermitidos: [],
    hasLift: false,
    hasScore: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      // Dois pares por padrão
      pares: [
        { peso: 1, variavel: this.getDefaultVariavel(), item: null },
        { peso: 1, variavel: this.getDefaultVariavel(), item: null },
      ],
      showValidation: false,
    };
  }

  componentDidUpdate(prevProps) {
    // Quando abrir novamente o modal, reseta os pares
    if (!prevProps.isOpen && this.props.isOpen) {
      this.setState({
        pares: [
          { peso: 1, variavel: this.getDefaultVariavel(), item: null },
          { peso: 1, variavel: this.getDefaultVariavel(), item: null },
        ],
        showValidation: false,
      });
    }
  }

  getDefaultVariavel() {
    // Retorna Meta como padrão (será validado pelo SmartVariableCalculationSelector)
    return { id: 'Meta', descricao: 'Meta' };
  }

  handleChangeVariavel(idx, value) {
    const pares = [...this.state.pares];
    pares[idx].variavel = value;
    this.setState({ pares });
  }

  handleChangeItem(idx, value) {
    const pares = [...this.state.pares];
    pares[idx].item = value;
    this.setState({ pares });
  }

  handleChangePeso(idx, value) {
    const pares = [...this.state.pares];
    pares[idx].peso = value;
    this.setState({ pares });
  }

  handleAddPair() {
    const pares = [...this.state.pares, { peso: 1, variavel: this.getDefaultVariavel(), item: null }];
    this.setState({ pares });
  }

  handleRemovePair(idx) {
    const pares = this.state.pares.filter((_, i) => i !== idx);
    this.setState({ pares });
  }

  // Verifica se a variável precisa de Item
  variavelRequerItem(variavelId) {
    const variaveisSemItem = [
      'ScoreFuncao', 'ScoreCategoria', 'PontosPorFaixaTransformada',
      'ScoreHierarquiaArea', 'ScoreHierarquiaColaborador', 'notaMetaIndividual'
    ];
    return !variaveisSemItem.includes(variavelId);
  }

  buildExpression() {
    const { tipo } = this.props;
    const paresValidos = this.state.pares.filter((par) => {
      if (this.variavelRequerItem(par.variavel.id)) {
        return par.item;
      }
      return true;
    });

    if (!paresValidos.length) return '';

    // Tratamento específico para média ponderada
    if (tipo === 'mediaPonderada') {
      const termos = [];
      let somaPesos = 0;

      paresValidos.forEach((par) => {
        const peso = Number(par.peso) || 0;
        if (peso === 0) return; // ignora pesos zero

        const termoVariavel = this.variavelRequerItem(par.variavel.id)
          ? `${par.variavel.id}([${par.item.idIntegracao}])`
          : `${par.variavel.id}()`;

        termos.push(`${peso}*${termoVariavel}`);
        somaPesos += peso;
      });

      if (!termos.length || somaPesos === 0) return '';

      const numerador = termos.join('+');
      return `(${numerador})/${Number(somaPesos.toFixed(2))}`;
    }

    // Tratamento para média simples, soma e produto (lógica existente)
    const args = paresValidos.map((par) => {
      if (this.variavelRequerItem(par.variavel.id)) {
        return `${par.variavel.id}([${par.item.idIntegracao}])`;
      } else {
        return `${par.variavel.id}()`;
      }
    });

    if (tipo === 'media') {
      const soma = args.join('+');
      return `(${soma})/${args.length}`;
    }

    const joiner = tipo === 'produto' ? '*' : '+';
    return args.join(joiner);
  }

  buildPreview() {
    const { tipo } = this.props;
    const partes = this.state.pares.map((par) => {
      if (this.variavelRequerItem(par.variavel.id)) {
        const idItem = par.item ? par.item.idIntegracao : 'item';
        return `${par.variavel.id.toLowerCase()}([${idItem}])`;
      } else {
        return `${par.variavel.id.toLowerCase()}()`;
      }
    });

    if (tipo === 'media') {
      return `(${partes.join('+')})/${this.state.pares.length}`;
    }

    if (tipo === 'mediaPonderada') {
      const termos = this.state.pares.map((par) => {
        const peso = Number(par.peso) || 0;

        const termoVar = this.variavelRequerItem(par.variavel.id)
          ? `${par.variavel.id.toLowerCase()}([${par.item ? par.item.idIntegracao : 'item'}])`
          : `${par.variavel.id.toLowerCase()}()`;

        return `${peso}*${termoVar}`;
      });

      const somaPesos = this.state.pares.reduce((acc, par) => acc + (Number(par.peso) || 0), 0);
      return `(${termos.join('+')})/${Number(somaPesos.toFixed(2)) || 1}`;
    }

    const joiner = tipo === 'produto' ? '*' : '+';
    return partes.join(joiner);
  }

  validateFields() {
    // Só valida os dois primeiros pares
    const paresObrigatorios = this.state.pares.slice(0, 2);
    const paresValidos = paresObrigatorios.filter((par) => {
      if (this.variavelRequerItem(par.variavel.id)) {
        return par.item;
      }
      return true;
    });
    return paresValidos.length >= 2;
  }

  handleInsert = () => {
    if (!this.validateFields()) {
      this.setState({ showValidation: true });
      return;
    }

    const expr = this.buildExpression();
    if (expr) {
      this.props.onInsert(expr);
    }
  };

  renderAddButton() {
    return (
      <Button
        color="link"
        onClick={this.handleAddPair}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        className="p-0"
      >
        <span
          className="d-inline-flex align-items-center justify-content-center bg-primary"
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            marginRight: 8,
          }}
        >
          <LucideIcon icon="plus" size={16} color="#fff" />
        </span>
        <span className="font-weight-bold text-primary" style={{ fontWeight: 900 }}>
          <FormattedMessage id="adicionarValor" />
        </span>
      </Button>
    );
  }

  render() {
    const { 
      isOpen, 
      titulo, 
      onRequestClose, 
      tipo,
      // Props de contexto
      isItem,
      tipoItem,
      tipoEntidade,
      isCestaIndicadores,
      modulosPermitidos,
      hasLift,
      hasScore
    } = this.props;

    const { showValidation } = this.state;

    const labelMap = {
      soma: 'addValoresParaSomar',
      produto: 'addValoresParaMultiplicar',
      media: 'addValoresParaMedia',
      mediaPonderada: 'addValoresParaMediaPonderada',
    };
    
    const actions = [
      <Button key="btn-add" type="primary" onClick={this.handleInsert}>
        <FormattedMessage id="label.inserir" />
      </Button>,
    ];

    return (
      <Dialog
        isOpen={isOpen}
        title={titulo}
        width={650}
        height={450}
        actions={actions}
        onRequestClose={onRequestClose}
        padContent
        styleTitle={{ paddingBottom: 0, marginBottom: 0 }}
      >
        <p>
          <FormattedMessage id={labelMap[tipo] || 'addValoresParaSomar'} />
        </p>

        {this.state.pares.map((pair, idx) => (
          <Row key={idx} className="mb-2 align-items-center">
            {tipo === 'mediaPonderada' && (
              <Col md={2}>
                <InputNumber
                  min={0}
                  precision={2}
                  model={{
                    label: idx === 0 ? <FormattedMessage id="peso" /> : null,
                    value: pair.peso,
                    requestChange: (v) => this.handleChangePeso(idx, v),
                  }}
                />
              </Col>
            )}
            <Col md={this.variavelRequerItem(pair.variavel.id) ? (tipo === 'mediaPonderada' ? 4 : 6) : (tipo === 'mediaPonderada' ? 10 : 12)}>
              <SmartVariableCalculationSelector
                isItem={isItem}
                tipoItem={tipoItem}
                tipoEntidade={tipoEntidade}
                isCestaIndicadores={isCestaIndicadores}
                modulosPermitidos={modulosPermitidos}
                hasLift={hasLift}
                hasScore={hasScore}
                value={pair.variavel}
                onChange={(v) => this.handleChangeVariavel(idx, v)}
                showLabel={idx === 0}
              />
            </Col>
            {this.variavelRequerItem(pair.variavel.id) && (
              <Col md={tipo === 'mediaPonderada' ? 6 : 6}>
                <ItemLookup
                  model={{
                    label: idx === 0 ? this.props.intl.formatMessage({ id: 'item' }) : null,
                    value: pair.item,
                    requestChange: (v) => this.handleChangeItem(idx, v),
                    errors: showValidation && idx < 2 && !pair.item ? true : null,
                  }}
                  className="inline-block"
                />
              </Col>
            )}
          </Row>
        ))}

        {this.renderAddButton()}

        <h6 className="mt-3 mb-1">
          <FormattedMessage id="previaFuncao" />
        </h6>
        <FormulaPreview formula={this.buildPreview()} />
      </Dialog>
    );
  }
}

export default injectIntl(BasicFunctionModal); 